// src/components/pages/readyfortickets/step2-ReviewAndPayment.jsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { CiLocationOn, CiClock2 } from "react-icons/ci";
import { useStripe, useElements, CardElement, PaymentRequestButtonElement } from "@stripe/react-stripe-js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga4";
import { resetSelectedTicket } from "../../redux/auth/action/selectedTicketActions";
import { getUserTicketByTicketId } from "../../redux/auth/action/userTicketActions"; // Ensure this action is correctly defined
import apiClient from "../../apiClient/apiClient";

const ReviewAndPayment = ({ eventDetails, handleConfirmPayment }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [pageLoading, setPageLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [googlePaymentRequest, setGooglePaymentRequest] = useState(null);

  const dispatch = useDispatch();
  const selectedTicket = useSelector((state) => state.selectedTicket.selectedTicket);
  const { userTicket, loadingUserTicket, error: userTicketError } = useSelector((state) => state.singleUserTicket);

  useEffect(() => {
    if (stripe && selectedTicket && eventDetails.total) {
      const paymentRequestConfig = {
        country: 'GB',
        currency: 'gbp',
        total: {
          label: eventDetails.event_name || 'Event Payment',
          amount: Math.round(eventDetails.total * 100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      };

      const applePr = stripe.paymentRequest(paymentRequestConfig);
      applePr.canMakePayment().then(result => {
        if (result && result.applePay) {
          setPaymentRequest(applePr);
        }
      });

      const googlePr = stripe.paymentRequest(paymentRequestConfig);
      googlePr.canMakePayment().then(result => {
        if (result && !result.applePay) {
          setGooglePaymentRequest(googlePr);
        }
      });

      [applePr, googlePr].forEach(pr => {
        pr.on('paymentmethod', async (event) => {
          try {
            const accessToken = localStorage.getItem("access") || sessionStorage.getItem("access");
            
            const formData = {
              event: selectedTicket.event,
              ticket: selectedTicket.id,
              ticket_quantity: eventDetails.ticket_quantity,
              sub_total: parseFloat(eventDetails.sub_total).toFixed(2),
              total: parseFloat(eventDetails.total).toFixed(2),
              user: localStorage.getItem("user_id") || sessionStorage.getItem("user_id"),
            };

            const response = await apiClient.post("api/ticket-purchase/", formData, {
              headers: { Authorization: `Bearer ${accessToken}` },
            });

            const { client_secret } = response.data;

          const { error, paymentIntent } = await stripe.confirmCardPayment(
            client_secret,
            {
              payment_method: event.paymentMethod.id,
            },
          );

            if (error) {
              event.complete('fail');
              setErrorMessage(error.message);
            } else {
              event.complete('success');
              toast.success("Payment confirmed! Your booking is successful.");
              dispatch(getUserTicketByTicketId(eventDetails.event));
              handleConfirmPayment();

              ReactGA.event('purchase', {
                currency: 'GBP',
                value: eventDetails.total,
                items: [{
                  item_id: selectedTicket.id,
                  item_name: eventDetails.event_name,
                  price: eventDetails.price,
                  quantity: eventDetails.ticket_quantity,
                }],
              });
            }
          } catch (error) {
            event.complete('fail');
            setErrorMessage("Payment failed. Please try again.");
          }
        });
      });
    }
  }, [stripe, selectedTicket, eventDetails]);

  if (!selectedTicket) {
    return <p className="text-red-500">No ticket selected. Please go back and select a ticket.</p>;
  }

  const handlePayment = async () => {
    if (!selectedTicket) {
      setErrorMessage("Ticket data is not loaded yet. Please wait.");
      return;
    }

    if (pageLoading) {
      return; // Prevent duplicate submissions
    }

    setPageLoading(true);
    setErrorMessage(""); // Clear any previous errors

    try {
      const accessToken = localStorage.getItem("access") || sessionStorage.getItem("access");

      if (!accessToken) {
        setErrorMessage("Authentication accessToken not found. Please log in.");
        setPageLoading(false);
        return;
      }

      if (!stripe || !elements) {
        setErrorMessage("Stripe has not been properly initialized.");
        setPageLoading(false);
        return;
      }

      const formData = {
        event: eventDetails.event_id,
        ticket: selectedTicket.id,
        ticket_quantity: eventDetails.ticket_quantity,
        sub_total: parseFloat(eventDetails.sub_total).toFixed(2),
        total: parseFloat(eventDetails.total).toFixed(2),
        user: localStorage.getItem("user_id") || sessionStorage.getItem("user_id"),
      };

      console.log("[DEBUG] Form data: ", formData);

      // Disable the payment button to prevent multiple submissions
      const submitButton = document.querySelector('button[type="submit"]');
      if (submitButton) submitButton.disabled = true;

      // Add a unique purchase ID to prevent duplicates
      const uniqueId = Date.now().toString();
      formData.unique_id = uniqueId;

      const response = await apiClient.post("api/ticket-purchase/", formData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const clientSecret = response.data.client_secret;
      const purchaseId = response.data.id;

      if (!clientSecret) {
        // Free tickets don't need payment processing
        toast.success("Free ticket confirmed!", {
          toastId: 1,
        });

        dispatch(getUserTicketByTicketId(selectedTicket.id));
        handleConfirmPayment();
        setPageLoading(false);
        return;
      }

      const cardElement = elements.getElement(CardElement);
      const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: eventDetails?.event_name || "Unnamed Event",
          },
        },
      });

      if (paymentError) {
        setErrorMessage(paymentError.message);
        setPageLoading(false);
        return;
      }

      // Start polling for status instead of immediately assuming success
      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        toast.info("Payment is being processed. Please wait for confirmation...");
        pollForTicketStatus(purchaseId);
      } else {
        setErrorMessage("Payment could not be confirmed. Please try again.");
        setPageLoading(false);
      }
    } catch (error) {
      console.error("Error confirming payment", error);
      // Enhance error display to show actual server validation errors
      if (error.response && error.response.data) {
        console.error("Server validation errors:", error.response.data);
        setErrorMessage(JSON.stringify(error.response.data));
      } else {
        setErrorMessage("Payment failed. Please try again.");
      }
      setPageLoading(false);
    }
  };

  // Add polling function
  const pollForTicketStatus = async (purchaseId) => {
    let attempts = 0;
    const maxAttempts = 15;
    const pollInterval = 2000; // 2 seconds
    
    const checkStatus = async () => {
      if (attempts >= maxAttempts) {
        toast.error("Payment confirmation taking longer than expected. Please check your tickets in your account.");
        setPageLoading(false);
        return;
      }
      
      try {
        // Make sure we're using the correct API endpoint as defined in the backend URLs
        const response = await apiClient.get(`api/user/tickets/${purchaseId}/`);
        console.log("Polling ticket status:", response.data);
        
        if (response.data.payment_status === 'completed') {
          toast.success("Payment confirmed! Your ticket has been issued.");
          dispatch(getUserTicketByTicketId(eventDetails.event));
          handleConfirmPayment();
          setPageLoading(false);
          return;
        } else if (response.data.payment_status === 'failed') {
          toast.error("Payment failed. Please try again.");
          setPageLoading(false);
          return;
        }
        
        // Still pending, try again
        attempts++;
        setTimeout(checkStatus, pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        attempts++;
        setTimeout(checkStatus, pollInterval);
      }
    };
    
    // Start checking
    setTimeout(checkStatus, pollInterval);
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#32325d",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
      },
    },
    hidePostalCode: true,
  };

  const isTicketDataLoaded = selectedTicket && !pageLoading;

  console.log("Event details @ step 2: ", eventDetails);

  return (
    <div className="shadow-lg rounded-lg overflow-hidden bg-white border border-gray-100">
      {/* Header with brand colors */}
      <div className="bg-[#0A2533] p-5 flex justify-between items-center">
        <h2 className="font-poppins text-xl font-bold text-white">Secure Checkout</h2>
        <div className="flex items-center space-x-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span className="text-white text-sm">Secure Transaction</span>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex flex-col md:flex-row md:space-x-6 p-6" style={{backgroundColor: "#FFF8EB"}}>
        {/* Left Column - Event Details */}
        <div className="w-full md:w-1/2 bg-white rounded-lg p-5 mb-6 md:mb-0 shadow-sm">
          <h3 className="text-xl font-semibold text-[#0A2533] mb-4 pb-2 border-b border-gray-200">
            Event Details
          </h3>
          
          <div className="space-y-3">
            <h4 className="text-2xl font-bold text-[#0A2533]">{eventDetails.event_name || "Unknown Event"}</h4>
            
            <div className="flex items-start space-x-2 text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5 text-[#F57C00]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <p className="font-medium">{eventDetails.venue || "Unknown Venue"}</p>
                <p>{eventDetails.address || ""}</p>
                <p>{eventDetails.post_code || ""}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-2 text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5 text-[#F57C00]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p>{eventDetails.start_date || "Unknown Date"}</p>
                <p>{eventDetails.time || "Unknown Time"}</p>
              </div>
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <h5 className="font-semibold text-[#0A2533] mb-2">Ticket Information</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">Ticket Type:</div>
                <div className="font-medium">
                  {selectedTicket?.ticket_category
                    ? selectedTicket.ticket_category.charAt(0).toUpperCase() + selectedTicket.ticket_category.slice(1)
                    : "Standard Ticket"}
                </div>
                
                <div className="text-gray-500">Quantity:</div>
                <div className="font-medium">{eventDetails.ticket_quantity || 1}</div>
                
                <div className="text-gray-500">Price per Ticket:</div>
                <div className="font-medium">£{eventDetails.price?.toFixed(2) || 0}</div>
                
                <div className="text-gray-500">Subtotal:</div>
                <div className="font-medium">£{eventDetails.sub_total?.toFixed(2) || 0}</div>
              </div>
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-[#0A2533]">Total:</span>
                <span className="text-xl font-bold text-[#F57C00]">
                  {eventDetails.total === 0 ? "Free" : `£${eventDetails.total?.toFixed(2) || 0}`}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column - Payment Form */}
        <div className="w-full md:w-1/2">
          <h3 className="text-xl font-semibold text-[#0A2533] mb-4 pb-2 border-b border-gray-200">
            Payment Method
          </h3>
          
          {/* Digital Wallet Buttons */}
          {(paymentRequest || googlePaymentRequest) && (
            <>
              <div className="space-y-3 mb-6">
                {/* Apple Pay Button */}
                {paymentRequest && (
                  <div>
                    <PaymentRequestButtonElement
                      options={{
                        paymentRequest,
                        style: {
                          paymentRequestButton: {
                            type: 'buy',
                            theme: 'dark'
                          },
                        },
                      }}
                    />
                  </div>
                )}
                
                {/* Google Pay Button */}
                {googlePaymentRequest && (
                  <div>
                    <PaymentRequestButtonElement
                      options={{
                        paymentRequest: googlePaymentRequest,
                        style: {
                          paymentRequestButton: {
                            type: 'buy',
                            theme: 'dark'
                          },
                        },
                      }}
                    />
                  </div>
                )}
              </div>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-[#FFF8EB] px-4 text-sm text-gray-500">Or pay with card</span>
                </div>
              </div>
            </>
          )}
          
          {/* Card Payment Form */}
          <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
            <label className="block text-sm font-medium text-[#0A2533] mb-2">
              Card Details
            </label>
            <div className="p-3 border border-gray-300 rounded-md mb-4">
              <CardElement options={cardElementOptions} />
            </div>
            
            {errorMessage && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                </div>
              </div>
            )}
            
            <button
              className={`w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-white font-medium ${!isTicketDataLoaded || pageLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#F57C00] hover:bg-[#E67000] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F57C00]'} transition duration-150`}
              onClick={handlePayment}
              disabled={!stripe || pageLoading || !isTicketDataLoaded}
            >
              {pageLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : eventDetails.total === 0 ? "Confirm Free Ticket" : `Pay £${eventDetails.total?.toFixed(2) || 0}`}
            </button>
            
            <p className="mt-4 text-center text-sm text-gray-500">
              Your payment is secured with SSL encryption.
            </p>
          </div>
        </div>
      </div>
      
      <ToastContainer />
    </div>
  );
};

export default ReviewAndPayment;